import React from 'react';
import { Link } from 'react-router-dom';
import '../../features/brand/cwm-tokens.css';

/**
 * App-wide error boundary rendered inside the unified AppShell so one
 * broken screen doesn't blank the entire app. Matches the brand (Nunito,
 * bubble-blue, cwm-sheet card), offers a reload + dashboard CTA, and
 * prints the error details in a collapsible block so the user can share a
 * meaningful bug report.
 *
 * Only class components can catch render errors — keep it small.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error, info: null };
  }

  componentDidCatch(error, info) {
    // Surface full stack to the console so devs can debug; keep the UI
    // message calm and actionable.
    // eslint-disable-next-line no-console
    console.error('[cwm] error boundary caught', error, info);
    this.setState({ info });
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{ minHeight: '60vh', padding: '48px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="cwm-sheet" style={{
          maxWidth: 520, width: '100%', padding: 28, textAlign: 'center',
          background: 'linear-gradient(160deg, #fff, var(--cwm-blue-pale))',
        }}>
          <div style={{
            width: 56, height: 56, margin: '0 auto 14px', borderRadius: 18,
            background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)',
            display: 'grid', placeItems: 'center',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.3 3.86L1.82 18a2 2 0 001.72 3h16.92a2 2 0 001.72-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <path d="M12 9v4M12 17h.01"/>
            </svg>
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.015em' }}>
            Something broke on our side.
          </h1>
          <p style={{ margin: '0 0 18px', fontSize: 14, fontWeight: 500, color: 'var(--cwm-gray-700)', lineHeight: 1.55 }}>
            This screen hit an error we didn't expect. The rest of the app is fine — try again, or jump back to your dashboard.
          </p>

          <div style={{ display: 'inline-flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button type="button" onClick={this.reset} className="cwm-btn" style={{ padding: '10px 20px' }}>
              Try again
            </button>
            <Link to="/unified" onClick={this.reset} className="cwm-btn-secondary" style={{ padding: '10px 20px', textDecoration: 'none' }}>
              Back to start
            </Link>
          </div>

          <details style={{ marginTop: 8, textAlign: 'left' }}>
            <summary style={{ fontSize: 12, fontWeight: 800, color: 'var(--cwm-gray-500)', cursor: 'pointer', padding: '4px 0', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Error details
            </summary>
            <pre style={{
              margin: '10px 0 0',
              padding: '12px 14px',
              background: 'var(--cwm-gray-50)',
              border: '1px solid var(--cwm-gray-100)',
              borderRadius: 12,
              fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              color: 'var(--cwm-gray-700)',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              maxHeight: 200, overflowY: 'auto',
            }}>
              {String(error?.message || error)}
              {info?.componentStack ? '\n' + info.componentStack : ''}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
